import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import {
  appApi,
  useGetEquipmentListQuery,
  useGetEquipmentDetailQuery,
  useGetVehiclesQuery,
} from "../../../../store/appApi";
import {
  logServiceApi,
  logVehicleServiceApi,
} from "../../../../services/equipementService";

// Vehicle service types — the dropdown is only shown for vehicle path
const VEHICLE_SERVICE_TYPES = ["Kilometer", "Time-Based", "Usage-Based"];

const metricLabelFor = (serviceMode) => {
  switch (serviceMode) {
    case "Hour-Based":       return "Hours Operated";
    case "Fuel Consumption": return "Fuel (Litres)";
    case "Kilometer":        return "Current Mileage (km)";
    case "Time-Based":       return "Total Hours";
    default:                 return "Metric Value";
  }
};

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const FORM_EMPTY = {
  eqpLastServiced: new Date(),
  eqpNextServiceDue: null,
  eqpLocationShift: "",
  eqpServiceType: "",
  metricValue: "",
  eqpServiceCost: "",
  eqpWarrantyExpiry: null,
  eqpNotes: "",
  alertIntervalMonths: "",
  alertFuelThreshold: "",
};

// ─── Equipment service form + history ────────────────────────────────────────

const EquipmentServiceSection = ({ targetId }) => {
  const dispatch = useDispatch();
  const { data, isLoading, isError } = useGetEquipmentDetailQuery(targetId);
  const equipment = data?.equipment || null;
  const [form, setForm] = useState(FORM_EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.eqpLastServiced) {
      Swal.fire("Validation", "Please select the last serviced date.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      await logServiceApi(targetId, {
        eqpLastServiced: form.eqpLastServiced,
        eqpNextServiceDue: form.eqpNextServiceDue || undefined,
        eqpLocationShift: form.eqpLocationShift || undefined,
        eqpServiceType: equipment?.serviceMode, // locked to equipment's serviceMode
        metricValue: form.metricValue !== "" ? Number(form.metricValue) : undefined,
        eqpServiceCost: Number(form.eqpServiceCost) || 0,
        eqpWarrantyExpiry: form.eqpWarrantyExpiry || undefined,
        eqpNotes: form.eqpNotes || undefined,
        alertIntervalMonths: form.alertIntervalMonths !== "" ? Number(form.alertIntervalMonths) : undefined,
        alertFuelThreshold: form.alertFuelThreshold !== "" ? Number(form.alertFuelThreshold) : undefined,
      });
      dispatch(appApi.util.invalidateTags(["Equipment"]));
      Swal.fire({ icon: "success", title: "Service record added.", timer: 1500, showConfirmButton: false });
      setForm(FORM_EMPTY);
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Failed to log service record.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>;
  if (isError || !equipment) return <div className="alert alert-warning">Failed to load equipment data.</div>;

  return (
    <>
      <div className="alert alert-info py-2 mb-3 d-flex justify-content-between align-items-center">
        <span>
          <i className="la la-cogs me-2" />
          <strong>{equipment.eqpName}</strong>
          {equipment.eqpNumber && <span className="ms-2 text-muted">· {equipment.eqpNumber}</span>}
          <span className="ms-2 text-muted">· {equipment.projectId?.projectName || "No project"}</span>
        </span>
        <Link to={`/progress/equipement/${targetId}/view`} className="btn btn-xs btn-outline-primary">
          View Full Detail
        </Link>
      </div>

      <div className="card mb-4">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="card-title mb-0">Log New Service — Equipment</h5>
          {equipment?.serviceMode && (
            <span className={`badge ${equipment.serviceMode === "Hour-Based" ? "badge-info" : "badge-warning"} light`}>
              <i className={`la ${equipment.serviceMode === "Hour-Based" ? "la-clock-o" : "la-tint"} me-1`} />
              {equipment.serviceMode}
            </span>
          )}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Last Serviced Date <span className="text-danger">*</span></label>
                <DatePicker
                  selected={form.eqpLastServiced}
                  onChange={(d) => setForm((prev) => ({ ...prev, eqpLastServiced: d }))}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                />
              </div>

              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Next Service Due</label>
                <DatePicker
                  selected={form.eqpNextServiceDue}
                  onChange={(d) => setForm((prev) => ({ ...prev, eqpNextServiceDue: d }))}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                  isClearable
                />
              </div>

              <div className="col-md-3 form-group">
                <label className="font-weight-bold">{metricLabelFor(equipment?.serviceMode)}</label>
                <input
                  type="number"
                  className="form-control"
                  name="metricValue"
                  value={form.metricValue}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  placeholder="0"
                />
              </div>

              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Present Location / Site</label>
                <input
                  type="text"
                  className="form-control"
                  name="eqpLocationShift"
                  value={form.eqpLocationShift}
                  onChange={handleChange}
                  placeholder="e.g. Site A, Warehouse"
                />
              </div>

              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Service Cost (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  name="eqpServiceCost"
                  value={form.eqpServiceCost}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  placeholder="0"
                />
              </div>

              <div className="col-md-3 form-group">
                <label className="font-weight-bold">Warranty Expiry</label>
                <DatePicker
                  selected={form.eqpWarrantyExpiry}
                  onChange={(d) => setForm((prev) => ({ ...prev, eqpWarrantyExpiry: d }))}
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                  isClearable
                />
              </div>

              <div className="col-md-12 form-group">
                <label className="font-weight-bold">Notes / Remarks</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name="eqpNotes"
                  value={form.eqpNotes}
                  onChange={handleChange}
                  placeholder="Service remarks, observations…"
                />
              </div>

              {/* Alert config — driven by equipment's serviceMode */}
              {equipment?.serviceMode === "Hour-Based" && (
                <div className="col-md-3 form-group">
                  <label className="font-weight-bold">Alert after (hours)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="alertIntervalMonths"
                    value={form.alertIntervalMonths}
                    onChange={handleChange}
                    min="1"
                    placeholder="e.g. 250"
                  />
                  <small className="text-muted">Trigger alert after N cumulative hours</small>
                </div>
              )}

              {equipment?.serviceMode === "Fuel Consumption" && (
                <div className="col-md-3 form-group">
                  <label className="font-weight-bold">Alert Threshold (litres)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="alertFuelThreshold"
                    value={form.alertFuelThreshold}
                    onChange={handleChange}
                    min="1"
                    placeholder="e.g. 500"
                  />
                  <small className="text-muted">Trigger alert when cumulative fuel ≥ this</small>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <><span className="spinner-border spinner-border-sm mr-2" role="status" />Saving…</>
              ) : (
                "Log Service"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Service History */}
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Service History</h5>
        </div>
        <div className="card-body">
          {!equipment.serviceHistory || equipment.serviceHistory.length === 0 ? (
            <p className="text-muted text-center py-3">No service records yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover verticle-middle">
                <thead className="thead-dark">
                  <tr>
                    <th>#</th>
                    <th>Last Serviced</th>
                    <th>Location / Site</th>
                    <th>Service Type</th>
                    <th>Metric</th>
                    <th>Service Cost</th>
                    <th>Warranty Expiry</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {equipment.serviceHistory.map((s, i) => (
                    <tr key={s._id || i}>
                      <td>{i + 1}</td>
                      <td className="small">{s.date ? moment(s.date).format("DD MMM YYYY") : "—"}</td>
                      <td className="small">{s.eqpLocationShift || "—"}</td>
                      <td><span className="badge badge-primary light">{s.serviceType || "—"}</span></td>
                      <td className="small">{s.metricValue != null ? fmt(s.metricValue) : "—"}</td>
                      <td className="small text-success fw-semibold">₹{fmt(s.serviceCost)}</td>
                      <td className="small">{s.warrantyExpiry ? moment(s.warrantyExpiry).format("DD MMM YYYY") : "—"}</td>
                      <td className="small text-muted">{s.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Vehicle service form ─────────────────────────────────────────────────────

const VehicleServiceSection = ({ vehicleId, vehicleLabel }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(FORM_EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.eqpLastServiced) {
      Swal.fire("Validation", "Please select the last serviced date.", "warning");
      return;
    }
    if (!form.eqpServiceType) {
      Swal.fire("Validation", "Please select a service type.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      await logVehicleServiceApi(vehicleId, {
        eqpLastServiced: form.eqpLastServiced,
        eqpNextServiceDue: form.eqpNextServiceDue || undefined,
        eqpLocationShift: form.eqpLocationShift || undefined,
        eqpServiceType: form.eqpServiceType,
        metricValue: form.metricValue !== "" ? Number(form.metricValue) : undefined,
        eqpServiceCost: Number(form.eqpServiceCost) || 0,
        eqpWarrantyExpiry: form.eqpWarrantyExpiry || undefined,
        eqpNotes: form.eqpNotes || undefined,
      });
      dispatch(appApi.util.invalidateTags(["Expenses"]));
      Swal.fire({ icon: "success", title: "Vehicle service logged to Finance.", timer: 1500, showConfirmButton: false });
      setForm(FORM_EMPTY);
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Failed to log vehicle service.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">Log New Service — Vehicle</h5>
        {vehicleLabel && <span className="badge badge-info">{vehicleLabel}</span>}
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">
          Vehicle service cost is logged directly to Finance (no sub-document stored on Vehicle).
        </p>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-3 form-group">
              <label className="font-weight-bold">Last Serviced Date <span className="text-danger">*</span></label>
              <DatePicker
                selected={form.eqpLastServiced}
                onChange={(d) => setForm((prev) => ({ ...prev, eqpLastServiced: d }))}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                placeholderText="Select date"
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="font-weight-bold">Next Service Due</label>
              <DatePicker
                selected={form.eqpNextServiceDue}
                onChange={(d) => setForm((prev) => ({ ...prev, eqpNextServiceDue: d }))}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                placeholderText="Select date"
                isClearable
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="font-weight-bold">Service Type <span className="text-danger">*</span></label>
              <select
                className="form-control"
                name="eqpServiceType"
                value={form.eqpServiceType}
                onChange={handleChange}
              >
                <option value="">-- Select Type --</option>
                {VEHICLE_SERVICE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {form.eqpServiceType && (
              <div className="col-md-3 form-group">
                <label className="font-weight-bold">{metricLabelFor(form.eqpServiceType)}</label>
                <input
                  type="number"
                  className="form-control"
                  name="metricValue"
                  value={form.metricValue}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  placeholder="0"
                />
              </div>
            )}

            <div className="col-md-3 form-group">
              <label className="font-weight-bold">Present Location / Site</label>
              <input
                type="text"
                className="form-control"
                name="eqpLocationShift"
                value={form.eqpLocationShift}
                onChange={handleChange}
                placeholder="e.g. Site A, Warehouse"
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="font-weight-bold">Service Cost (₹)</label>
              <input
                type="number"
                className="form-control"
                name="eqpServiceCost"
                value={form.eqpServiceCost}
                onChange={handleChange}
                min="0"
                step="any"
                placeholder="0"
              />
            </div>

            <div className="col-md-3 form-group">
              <label className="font-weight-bold">Warranty Expiry</label>
              <DatePicker
                selected={form.eqpWarrantyExpiry}
                onChange={(d) => setForm((prev) => ({ ...prev, eqpWarrantyExpiry: d }))}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                placeholderText="Select date"
                isClearable
              />
            </div>

            <div className="col-md-12 form-group">
              <label className="font-weight-bold">Notes / Remarks</label>
              <textarea
                className="form-control"
                rows={2}
                name="eqpNotes"
                value={form.eqpNotes}
                onChange={handleChange}
                placeholder="Service remarks, observations…"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? (
              <><span className="spinner-border spinner-border-sm mr-2" role="status" />Saving…</>
            ) : (
              "Log Vehicle Service"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const EquipmentServiceLogs = () => {
  const { id } = useParams();
  const { data: listData, isLoading: listLoading } = useGetEquipmentListQuery();
  const { data: vehicleData, isLoading: vehicleLoading } = useGetVehiclesQuery();
  const equipmentItems = listData?.items || [];
  const vehicleItems = vehicleData?.data || [];

  const [assetType, setAssetType] = useState("equipment"); // "equipment" | "vehicle"
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(id || "");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");

  const targetEquipmentId = id || selectedEquipmentId;
  const selectedVehicle = vehicleItems.find((v) => v._id === selectedVehicleId);
  const vehicleLabel = selectedVehicle
    ? `${selectedVehicle.vehicleMake} ${selectedVehicle.vehicleNumber}`
    : "";

  return (
    <>
      <PageTitle activeMenu="Service Logs" motherMenu="Daily Progress / Equipment" />

      <div className="mb-3">
        <Link to="/progress/equipement/list" className="btn btn-sm btn-outline-secondary">
          &larr; Back to List
        </Link>
      </div>

      {/* Asset Type Selector */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-3 form-group mb-0">
              <label className="font-weight-bold d-block mb-2">Asset Type</label>
              <div className="d-flex gap-4">
                <div className="custom-control custom-radio">
                  <input
                    type="radio"
                    className="custom-control-input"
                    id="type-equipment"
                    name="assetType"
                    value="equipment"
                    checked={assetType === "equipment"}
                    onChange={() => setAssetType("equipment")}
                  />
                  <label className="custom-control-label" htmlFor="type-equipment">Equipment / Machinery</label>
                </div>
                <div className="custom-control custom-radio">
                  <input
                    type="radio"
                    className="custom-control-input"
                    id="type-vehicle"
                    name="assetType"
                    value="vehicle"
                    checked={assetType === "vehicle"}
                    onChange={() => setAssetType("vehicle")}
                  />
                  <label className="custom-control-label" htmlFor="type-vehicle">Vehicle</label>
                </div>
              </div>
            </div>

            {assetType === "equipment" && (
              <div className="col-md-5 form-group mb-0">
                <label className="font-weight-bold">
                  Select Equipment <span className="text-danger">*</span>
                </label>
                {listLoading ? (
                  <div className="spinner-border spinner-border-sm text-primary" />
                ) : (
                  <select
                    className="form-control"
                    value={targetEquipmentId}
                    onChange={(e) => setSelectedEquipmentId(e.target.value)}
                    disabled={!!id}
                  >
                    <option value="">-- Choose equipment --</option>
                    {equipmentItems.map((eq) => (
                      <option key={eq._id} value={eq._id}>
                        {eq.eqpName}
                        {eq.eqpNumber ? ` (${eq.eqpNumber})` : ""}
                        {" — "}
                        {eq.projectName || "No project"}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {assetType === "vehicle" && (
              <div className="col-md-5 form-group mb-0">
                <label className="font-weight-bold">
                  Select Vehicle <span className="text-danger">*</span>
                </label>
                {vehicleLoading ? (
                  <div className="spinner-border spinner-border-sm text-primary" />
                ) : (
                  <select
                    className="form-control"
                    value={selectedVehicleId}
                    onChange={(e) => setSelectedVehicleId(e.target.value)}
                  >
                    <option value="">-- Choose vehicle --</option>
                    {vehicleItems.map((v) => (
                      <option key={v._id} value={v._id}>
                        {v.vehicleMake} {v.vehicleNumber}
                        {v.vehicleModel ? ` (${v.vehicleModel})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Render form section based on asset type + selection */}
      {assetType === "equipment" && targetEquipmentId && (
        <EquipmentServiceSection key={targetEquipmentId} targetId={targetEquipmentId} />
      )}

      {assetType === "vehicle" && selectedVehicleId && (
        <VehicleServiceSection key={selectedVehicleId} vehicleId={selectedVehicleId} vehicleLabel={vehicleLabel} />
      )}

      {assetType === "equipment" && !targetEquipmentId && (
        <div className="text-center text-muted py-5">
          <i className="la la-arrow-up fs-3 d-block mb-2" />
          Select an equipment above to log a service record.
        </div>
      )}

      {assetType === "vehicle" && !selectedVehicleId && (
        <div className="text-center text-muted py-5">
          <i className="la la-arrow-up fs-3 d-block mb-2" />
          Select a vehicle above to log a service record.
        </div>
      )}
    </>
  );
};

export default EquipmentServiceLogs;
